import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Coins, ArrowRight } from "lucide-react";
import { ROADMAP_PHASES as PHASES } from "@/lib/roadmapData";

const StatusPill = ({ status }) => (
  <span
    data-testid={`phase-status-${status.toLowerCase()}`}
    className={`tag !text-[10px] !px-2.5 !py-1 inline-flex items-center gap-1 ${status === "COMPLETED" ? "bg-neon text-black" : "bg-gold text-black"}`}>
    {status === "COMPLETED" && <Check className="w-3 h-3" />} {status}
  </span>
);

export default function Roadmap() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14" data-testid="roadmap-page">
      <span className="tag bg-danger text-white" data-testid="roadmap-planned-banner">PLANNED · SUBJECT TO CHANGE</span>
      <h1 className="headline text-cream text-4xl sm:text-6xl mt-5">$ROBBED <span className="text-neon">ROADMAP</span></h1>
      <p className="font-mono text-dim mt-4 max-w-2xl text-sm sm:text-base">
        The story so far and where we're headed — from a meme community to a multichain launch.
        Everything beyond Phase 1 is a <span className="text-cream font-bold">plan, not a promise</span>: no dates, no
        deployed contracts, no live liquidity yet.
      </p>

      <div className="relative mt-12 pl-6 sm:pl-8">
        {/* timeline spine */}
        <div className="absolute left-[9px] sm:left-[13px] top-2 bottom-2 w-1 bg-charcoal" aria-hidden="true" />
        <div className="space-y-6">
          {PHASES.map((p, i) => (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.05 }}
              className="relative"
              data-testid={`phase-${p.n}`}>
              {/* node */}
              <span className={`absolute -left-[26px] sm:-left-[34px] top-5 w-5 h-5 rounded-full border-3 border-black ${p.status === "COMPLETED" ? "bg-neon" : "bg-ink3"}`} />
              <div className={`comic-card p-5 sm:p-6 ${p.status === "COMPLETED" ? "border-neon" : ""}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 grid place-items-center bg-ink3 border-3 border-black text-neon shrink-0">
                      <p.Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="font-mono text-[11px] uppercase text-dim tracking-widest">Phase {p.n}</p>
                      <h3 className="font-display font-900 uppercase text-cream text-lg sm:text-xl leading-tight">{p.title}</h3>
                    </div>
                  </div>
                  <StatusPill status={p.status} />
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {p.items.map((it, j) => {
                    const label = typeof it === "string" ? it : it.label;
                    const to = typeof it === "string" ? null : it.to;
                    const base = "inline-flex items-center gap-1.5 font-mono text-[11px] sm:text-xs px-3 py-1.5 border-2";
                    const done = p.status === "COMPLETED";
                    const cls = done
                      ? "border-neon/50 bg-neon/10 text-cream"
                      : "border-charcoal bg-ink2 text-cream/80";
                    return to ? (
                      <Link key={j} to={to} className={`${base} ${cls} hover:border-neon hover:text-neon transition-colors`} data-testid={`phase-${p.n}-link-tokenomics`}>
                        {done && <Check className="w-3 h-3 text-neon" />} {label} <ArrowRight className="w-3 h-3" />
                      </Link>
                    ) : (
                      <span key={j} className={`${base} ${cls}`}>
                        {done && <Check className="w-3 h-3 text-neon" />} {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="comic-card p-5 sm:p-6 mt-10 bg-ink3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h4 className="font-display font-900 text-neon uppercase text-sm">See the planned tokenomics</h4>
          <p className="font-mono text-xs text-dim mt-1">One unified 1,000,000,000 $ROBBED supply across Solana + Robinhood Chain — all PLANNED.</p>
        </div>
        <Link to="/tokenomics" className="btn-neon !py-2 !px-4 !text-sm shrink-0" data-testid="roadmap-tokenomics-cta">
          <Coins className="w-4 h-4" /> View Tokenomics
        </Link>
      </div>

      <p className="font-mono text-[11px] text-dim mt-8 leading-relaxed">
        This roadmap is directional and <span className="text-cream">subject to change</span>. Nothing here is a
        financial promise, a confirmed date, or a live blockchain deployment. $ROBBED is PRE-LAUNCH.
      </p>
    </div>
  );
}
